/**
 * Dialer — HX communications hub. Keypad + line status on the left,
 * recent calls / quick dial / quick actions on the right.
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useCall, formatDuration } from "@/context/CallContext";
import { useAuth } from "@/context/AuthContext";
import { StatusPill, timeAgo } from "@/pages/Dashboard";
import api from "@/lib/api";
import {
  Phone, Backspace, WarningCircle, CheckCircle, ArrowSquareOut,
  ChatText, AddressBook, ListBullets, Microphone, CaretRight,
  ClockCounterClockwise, Headset, ArrowUpRight, X, Star,
} from "@phosphor-icons/react";
import { HxCard, HxCardHead, HxTile, HxChip, HxEmpty, HxSkeleton, HX_COLORS as C } from "@/hx";

const KEYS = [["1",""],["2","ABC"],["3","DEF"],["4","GHI"],["5","JKL"],["6","MNO"],["7","PQRS"],["8","TUV"],["9","WXYZ"],["*",""],["0","+"],["#",""]];

const initials = (name) => (name || "?").trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase();

export default function DialerPage() {
  const { startCall } = useCall();
  const [n, setN] = useState("");
  const [acct, setAcct] = useState(null);
  const [recent, setRecent] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadStatus = useCallback(() => {
    api.get("/twilio/account-status").then(r => setAcct(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    loadStatus();
    Promise.all([
      api.get("/calls?limit=6").catch(() => ({ data: [] })),
      api.get("/contacts").catch(() => ({ data: [] })),
    ]).then(([calls, cts]) => {
      setRecent(calls.data || []);
      setContacts((cts.data || []).slice(0, 5));
    }).finally(() => setLoading(false));
  }, [loadStatus]);

  const { user } = useAuth();
  // For agents/supervisors, THEIR assigned Twilio number is the correct caller
  // ID to display — not the org's platform-default `acct.twilio_number` from
  // /twilio/account-status. Admins/owners still see the org-wide number.
  const myNumber = user?.own_seat?.phone_e164 || acct?.twilio_number;
  const isTrial = acct?.type === "Trial";
  const verified = acct?.verified_caller_ids || [];
  const verifiedNums = verified.map(v => v.phone_number);
  const rejected = acct?.recent_rejected_callers || [];
  const consoleUrl = acct?.verified_callers_console_url || "https://console.twilio.com/us1/develop/phone-numbers/manage/verified";
  const normalized = n.replace(/[^\d+]/g, "");
  const isVerified = verifiedNums.includes(normalized);

  return (
    <div className="hbf-hx" data-testid="dialer-page">
      {/* ── Hero ─────────────────────────────────────────────── */}
      <header className="hx-hero hx-up" data-testid="dialer-hero">
        <div>
          <div className="hx-hero__eyebrow"><Headset size={16} weight="duotone" color={C.blue} /> Communications hub</div>
          <h1 className="hx-hero__title">Place a call, <em>instantly</em></h1>
          <p className="hx-hero__sub">Dial out from your browser, jump back into recent conversations, and keep your line healthy.</p>
        </div>
        <div className="hx-hero__cta">
          <div className="hx-linepill" data-testid="dialer-line-status">
            <span className="hx-linepill__dot" style={isTrial ? { background: "#F59E0B", boxShadow: "0 0 0 4px rgba(245,158,11,.14)" } : undefined} />
            <span>Line {acct ? "active" : "…"}</span>
            {myNumber && <span style={{ fontFamily: "var(--hx-mono)", fontSize: 12, color: "var(--hx-ink-2)" }} data-testid="dialer-my-number">{myNumber}</span>}
            {acct && <HxChip tone={isTrial ? "amber" : "green"}>{acct.type || "Full"}</HxChip>}
          </div>
        </div>
      </header>

      <div className="hx-grid-main hx-up hx-d2">
        {/* ── Left: keypad ───────────────────────────────────── */}
        <div className="hx-span5" style={{ display: "grid", gap: 16, alignContent: "start" }}>
          <HxCard data-testid="dialer-keypad-card">
            <HxCardHead
              title="Keypad"
              icon={<Phone size={15} weight="duotone" color={C.green} />}
              right={myNumber && (
                <span style={{ fontSize: 11, fontWeight: 700, color: "var(--hx-ink-3)" }}>
                  Your number · <span style={{ fontFamily: "var(--hx-mono)" }} data-testid="dialer-caller-id">{myNumber}</span>
                </span>
              )}
            />
            <div style={{ padding: "12px 24px 24px" }}>
              <input
                value={n}
                onChange={(e) => setN(e.target.value)}
                placeholder="Enter phone number"
                className="hx-dialinput"
                data-testid="dialer-page-input"
              />
              {isTrial && n && (
                <div style={{ marginTop: 10, fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, color: isVerified ? "#047857" : "#B45309" }}
                     data-testid="dialer-verify-status">
                  {isVerified
                    ? <><CheckCircle size={14} weight="fill" /> Verified — call should connect</>
                    : <><WarningCircle size={14} weight="fill" /> Not verified — Twilio trial will refuse this call</>}
                </div>
              )}
              <div className="grid grid-cols-3 gap-2.5" style={{ marginTop: 18 }}>
                {KEYS.map(([k, s]) => (
                  <button key={k} onClick={() => setN((v) => v + k)} className="hx-dialkey" data-testid={`dialer-page-key-${k}`}>
                    <span>{k}</span>
                    {s && <span className="sub">{s}</span>}
                  </button>
                ))}
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
                <button onClick={() => setN((v) => v.slice(0, -1))} className="hx-btn hx-btn--ghost" style={{ flex: 1, justifyContent: "center" }} data-testid="dialer-page-back">
                  <Backspace size={16} /> Back
                </button>
                <button
                  onClick={() => n && startCall({ to_number: n, to_name: "Unknown" })}
                  disabled={!n}
                  className="hx-btn hx-btn--call"
                  style={{ flex: 2, justifyContent: "center" }}
                  data-testid="dialer-page-call"
                >
                  <Phone size={17} weight="fill" /> Call
                </button>
              </div>
            </div>
          </HxCard>

          {isTrial && (
            <HxCard style={{ background: "linear-gradient(165deg, #FFF9EE, #FFFFFF 70%)", borderColor: "#F3DFB6" }} data-testid="dialer-trial-banner">
              <div style={{ padding: "18px 22px 20px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 13.5, fontWeight: 800, color: "#92400E" }}>
                  <WarningCircle size={17} weight="fill" /> Twilio Trial — verify caller numbers
                </div>
                <p style={{ fontSize: 12.5, color: "#7C5A18", marginTop: 8, lineHeight: 1.55 }}>
                  On Trial, every phone that calls <span style={{ fontFamily: "var(--hx-mono)" }}>{acct?.twilio_number}</span> and
                  every number you dial out must be verified in the Twilio Console. Unverified callers are rejected
                  with error 21264 before the call reaches Hello by Flow.
                </p>

                {rejected.length > 0 && (
                  <div style={{ marginTop: 14, background: "#fff", border: "1px solid #F5C2C2", borderRadius: 12, padding: "10px 14px" }} data-testid="dialer-rejected-section">
                    <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase", color: "#B91C1C", display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                      <X size={11} weight="bold" /> Recently rejected callers (7 days)
                    </div>
                    <ul style={{ display: "grid", gap: 5 }} data-testid="dialer-rejected-list">
                      {rejected.map(r => (
                        <li key={r.call_sid} style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 12, color: "#92400E" }}
                            data-testid={`dialer-rejected-${r.phone_number}`}>
                          <span style={{ fontFamily: "var(--hx-mono)" }}>{r.phone_number}</span>
                          <span style={{ fontSize: 10.5, color: "var(--hx-ink-3)" }}>{r.at ? new Date(r.at).toLocaleString() : ""}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {verifiedNums.length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase", color: "#7C5A18", marginBottom: 7 }}>Verified numbers · click to dial</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }} data-testid="dialer-verified-list">
                      {verifiedNums.map(v => (
                        <button key={v} onClick={() => setN(v)}
                                className="hx-chip hx-chip--amber"
                                style={{ fontFamily: "var(--hx-mono)", fontSize: 11.5, cursor: "pointer", border: "1px solid #F3DFB6" }}
                                data-testid={`dialer-verified-${v}`}>
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <a
                  href={consoleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hx-btn hx-btn--sm"
                  style={{ marginTop: 16, background: "#92400E", color: "#fff", borderColor: "transparent" }}
                  data-testid="dialer-verify-console-link"
                >
                  <ArrowSquareOut size={13} weight="bold" /> Open Twilio Console → Verify
                </a>
              </div>
            </HxCard>
          )}
        </div>

        {/* ── Right: hub rail ────────────────────────────────── */}
        <div className="hx-span7 hx-rail">
          <HxCard data-testid="dialer-recent-calls">
            <HxCardHead
              title="Recent conversations"
              icon={<ClockCounterClockwise size={15} weight="duotone" color={C.blue} />}
              right={<Link to="/app/calls" className="hx-card__link" data-testid="dialer-view-all-calls">View all <ArrowUpRight size={13} weight="bold" /></Link>}
            />
            {loading ? (
              <HxSkeleton height={46} count={4} style={{ padding: "8px 20px 18px" }} />
            ) : recent.length === 0 ? (
              <HxEmpty icon={<Phone size={22} weight="duotone" />} data-testid="dialer-recent-empty">
                No calls yet — your history will appear here.
              </HxEmpty>
            ) : (
              <div className="hx-feed">
                {recent.map((c, i) => (
                  <div key={c.id} className="hx-feed__row" style={{ animationDelay: `${i * 0.05}s` }} data-testid={`dialer-recent-call-${c.id}`}>
                    <div className="hx-feed__avatar">{initials(c.to_name)}</div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="hx-feed__who">{c.to_name || "Unknown"}</div>
                      <div className="hx-feed__what">{c.direction === "inbound" ? "Incoming" : "Outgoing"} · {c.to_number} · {c.duration ? formatDuration(c.duration) : timeAgo(c.started_at)}</div>
                    </div>
                    <StatusPill s={c.status} />
                    <button className="hx-dial__call" onClick={() => startCall({ to_number: c.to_number, to_name: c.to_name })} data-testid={`dialer-callback-${c.id}`}>
                      <Phone size={12} weight="fill" /> Call
                    </button>
                  </div>
                ))}
              </div>
            )}
          </HxCard>

          <HxCard data-testid="dialer-favorites">
            <HxCardHead
              title="Quick dial"
              icon={<Star size={15} weight="duotone" color={C.orange} />}
              right={<Link to="/app/contacts" className="hx-card__link">All contacts <ArrowUpRight size={13} weight="bold" /></Link>}
            />
            {loading ? (
              <HxSkeleton height={44} count={3} style={{ padding: "8px 20px 18px" }} />
            ) : contacts.length === 0 ? (
              <HxEmpty icon={<AddressBook size={22} weight="duotone" />} data-testid="dialer-favorites-empty">
                No contacts yet — add someone from Contacts.
              </HxEmpty>
            ) : (
              <div style={{ padding: "2px 10px 10px" }}>
                {contacts.map((c) => (
                  <div key={c.id} className="hx-dial" data-testid={`dialer-fav-${c.id}`}>
                    <div className="hx-feed__avatar">
                      {c.avatar_url ? <img src={c.avatar_url} alt="" style={{ width: "100%", height: "100%", borderRadius: 12, objectFit: "cover" }} /> : initials(c.name)}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div className="hx-feed__who" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.name}</div>
                      <div className="hx-feed__what">{c.phone}</div>
                    </div>
                    <button className="hx-dial__call" onClick={() => startCall({ to_number: c.phone, to_name: c.name })} data-testid={`dialer-fav-call-${c.id}`}>
                      <Phone size={12} weight="fill" /> Call
                    </button>
                  </div>
                ))}
              </div>
            )}
          </HxCard>

          <HxCard data-testid="dialer-quick-actions">
            <HxCardHead title="Quick actions" />
            <div style={{ padding: "6px 10px 12px" }}>
              <QA to="/app/sms"        tone="green"  icon={ChatText}    label="Send a message"         testid="dialer-qa-messages" />
              <QA to="/app/recordings" tone="violet" icon={Microphone}  label="Voicemail & recordings" testid="dialer-qa-voicemail" />
              <QA to="/app/contacts"   tone="blue"   icon={AddressBook} label="Add a contact"          testid="dialer-qa-contacts" />
              <QA to="/app/calls"      tone="orange" icon={ListBullets} label="View call history"      testid="dialer-qa-calls" />
            </div>
          </HxCard>
        </div>
      </div>
    </div>
  );
}

function QA({ to, tone, icon: Icon, label, testid }) {
  return (
    <Link to={to} className="hx-qa-row" data-testid={testid}>
      <HxTile tone={tone} size={36} radius={11} className="hx-qa-ic"><Icon size={17} weight="duotone" /></HxTile>
      {label}
      <CaretRight size={14} weight="bold" className="hx-qa-chev" />
    </Link>
  );
}
